/**
 * =============================================================================
 * Util: affiliate-phone-validation
 * Versão: 1.0.0
 *
 * Descrição:
 *   Funções compartilhadas de validação e normalização de telefone usadas pelos
 *   formulários de captura de indicação (landing pública e modal "Nova Indicação"
 *   no painel do afiliado). Extraído originalmente do `AffiliateLandingPage`
 *   (v1.3.0) para evitar duplicação e garantir uma única fonte de verdade.
 *
 *   Por que existe:
 *     - O validador legado `validatePhone` (input-validators.ts) rejeita formatos
 *       com DDI emitidos pelo `PhoneInput` (ex: +5562...).
 *     - Precisamos de regras únicas para BR (10/11 dígitos), BR com DDI 55
 *       (12/13), E.164 (8–15) e números especiais (0800/4003/400X/300X).
 *     - Defesa em profundidade no `normalize`: garante que o backend nunca
 *       receberá mais dígitos do que o usuário viu na tela (bug histórico do
 *       "1 final repetido").
 * =============================================================================
 */

/** Resultado padrão de validação (mesmo formato do input-validators). */
export interface AffiliatePhoneValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Valida o telefone considerando os formatos suportados pelo PhoneInput.
 *
 * Aceita:
 *  - Brasil sem DDI: 10 (fixo) ou 11 (celular) dígitos.
 *  - Brasil com DDI 55: 12 ou 13 dígitos (55 + DDD + número).
 *  - Internacional: 8 a 15 dígitos (E.164).
 *  - Especiais BR (sem DDI): 0800, 4003, 400X, 300X (7–11 dígitos).
 *
 * Rejeita:
 *  - Vazio.
 *  - Comprimento fora de 8–15 dígitos.
 *  - Sequência repetida (ex: 11111111111).
 *  - DDD brasileiro inválido (< 11) quando aplicável.
 */
export function validateAffiliatePhone(rawValue: string): AffiliatePhoneValidationResult {
  const digits = (rawValue || '').replace(/\D/g, '');

  if (digits.length === 0) {
    return { isValid: false, error: 'Por favor, insira um telefone válido com DDD' };
  }

  // Bloqueia sequências repetidas (anti-spam / dado inválido).
  if (/^(\d)\1+$/.test(digits)) {
    return { isValid: false, error: 'Telefone inválido' };
  }

  // Especiais BR (0800, 4003, 400X, 300X) — passam direto, sem DDI.
  if (
    digits.startsWith('0800') ||
    digits.startsWith('4003') ||
    digits.startsWith('400') ||
    digits.startsWith('300')
  ) {
    if (digits.length < 7 || digits.length > 11) {
      return { isValid: false, error: 'Telefone inválido' };
    }
    return { isValid: true };
  }

  // Faixa geral E.164 (cobre BR local, BR com DDI e internacional).
  if (digits.length < 8 || digits.length > 15) {
    return {
      isValid: false,
      error: 'Por favor, insira um telefone válido com DDD',
    };
  }

  // Quando for claramente brasileiro (10/11 sem DDI ou 12/13 com DDI 55), valida DDD.
  let dddCandidate: string | null = null;
  if (digits.length === 10 || digits.length === 11) {
    dddCandidate = digits.slice(0, 2);
  } else if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    dddCandidate = digits.slice(2, 4);
  }
  if (dddCandidate !== null) {
    const ddd = parseInt(dddCandidate, 10);
    if (Number.isNaN(ddd) || ddd < 11 || ddd > 99) {
      return { isValid: false, error: 'DDD inválido' };
    }
  }

  return { isValid: true };
}

/**
 * Normaliza o telefone para envio ao backend (defesa em profundidade).
 *
 * Regras:
 *   - Especiais BR (0800/4003/400X/300X): teto de 11 dígitos.
 *   - BR com DDI 55: teto de 13 dígitos (55 + DDD + 9 dígitos do celular).
 *   - Internacional/genérico (E.164): teto de 15 dígitos.
 *   - Retorna apenas dígitos (sem `+`, sem máscara).
 */
export function normalizeAffiliatePhoneForSubmit(value: string): string {
  const digits = (value || '').trim().replace(/\D/g, '');
  if (!digits) return '';

  if (
    digits.startsWith('0800') ||
    digits.startsWith('4003') ||
    digits.startsWith('400') ||
    digits.startsWith('300')
  ) {
    return digits.slice(0, 11);
  }

  if (digits.startsWith('55') && digits.length > 13) {
    return digits.slice(0, 13);
  }

  return digits.slice(0, 15);
}
